/**
 * CanonicalURL — single-source-of-truth canonical link injector.
 *
 * On every route change, this component rewrites <link rel="canonical">
 * (and the og:url / twitter:url OpenGraph tags) in the document head to
 * point to the canonical www.sptdigitalmarketing.com version of the
 * current path.
 *
 * Why this exists:
 *   Both https://sptdigitalmarketing.com and https://www.sptdigitalmarketing.com
 *   currently return 200 for every page — no server-side 301 is in place.
 *   Google treats them as separate URLs, which can split PageRank. This
 *   component tells Google "the canonical version of this page is the www
 *   one", so all backlinks and crawl signal consolidate to one URL.
 *
 * The canonical base is read from REACT_APP_CANONICAL_BASE_URL in
 * frontend/.env (falls back to the production www URL).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_BASE = (
  process.env.REACT_APP_CANONICAL_BASE_URL || 'https://sptdigitalmarketing.com'
).replace(/\/$/, '');

// Routes that must NEVER be indexed / canonicalized to a public URL.
// (admin, auth, dashboards — the robots.txt already Disallows them;
// we additionally emit a <meta name="robots" content="noindex,nofollow">)
const PRIVATE_PATH_PREFIXES = [
  '/admin',
  '/dashboard',
  '/jobs',
  '/customers',
  '/time-tracking',
  '/timer',
  '/expenses',
  '/invoices',
  '/users',
  '/settings',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/sweet-shop',
  '/sweet-shop-cart',
];

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeByQuery(selector) {
  const el = document.head.querySelector(selector);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

const CanonicalURL = () => {
  const location = useLocation();

  useEffect(() => {
    const isPrivate = PRIVATE_PATH_PREFIXES.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/')
    );

    if (isPrivate) {
      // Private area — tell search engines to stay out, strip any stale canonical
      upsertMeta('meta[name="robots"]', 'name', 'robots', 'noindex,nofollow');
      removeByQuery('link[rel="canonical"]');
      removeByQuery('meta[property="og:url"]');
      removeByQuery('meta[name="twitter:url"]');
      return;
    }

    // Public page — build canonical URL (strip trailing slash except for "/")
    const path = location.pathname === '/' ? '' : location.pathname.replace(/\/$/, '');
    const canonical = `${CANONICAL_BASE}${path}`;

    upsertLink('canonical', canonical);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[name="twitter:url"]', 'name', 'twitter:url', canonical);
    // Public page — remove any stale noindex
    const robotsMeta = document.head.querySelector('meta[name="robots"]');
    if (robotsMeta && /noindex/i.test(robotsMeta.getAttribute('content') || '')) {
      robotsMeta.setAttribute('content', 'index,follow');
    }
  }, [location.pathname]);

  return null;
};

export default CanonicalURL;
